import { useState } from "react";

import { DEFAULT_RANGE_DAYS, MAX_COMPARE, NO_PLACE, parseBranchParam, useFilters } from "../lib/filters";
import { isConfigured } from "../types";
import { daysAgo, isoDate, monthRange } from "../lib/format";
import { useUrlState } from "../lib/urlState";
import { BranchSearch } from "./BranchSearch";
import { Select } from "./Select";
import { Switch } from "./Switch";
import { DEFAULT_STAFF_PERCENT, MAX_STAFF_PERCENT, MIN_STAFF_PERCENT } from "../lib/staffPercent";
import { chartSeries, ui } from "../lib/ui";

const PRESETS: { label: string; range: () => { from: string; to: string } }[] = [
  { label: "7d", range: () => ({ from: daysAgo(6), to: isoDate(new Date()) }) },
  { label: "30d", range: () => ({ from: daysAgo(29), to: isoDate(new Date()) }) },
  { label: "90d", range: () => ({ from: daysAgo(DEFAULT_RANGE_DAYS), to: isoDate(new Date()) }) },
  { label: "180d", range: () => ({ from: daysAgo(179), to: isoDate(new Date()) }) },
  { label: "This month", range: () => monthRange(0) },
  { label: "Last month", range: () => monthRange(-1) },
];

/** The dashboard's filters: the date range (presets, then a custom pair —
 *  every panel below re-scopes to it) and, on the hub, the **branch rows**:
 *  one row per store on the dashboard, each with the colour its series get,
 *  plus a row to add another. No rows means every store in the drilled-into
 *  place (governorate → area). 460+ branches, so a row is a search, never a
 *  list. A branch PC is one store: only the range shows. */
export function FilterBar() {
  const {
    governorates,
    place,
    setPlace,
    branchesInPlace,
    range,
    setRange,
    includeDemo,
    setIncludeDemo,
    hasDemo,
    excludeStaff,
    setExcludeStaff,
    staffPercent,
    setStaffPercent,
    single,
    branches,
  } = useFilters();
  // Transient: picking a result writes the branch to the URL, which is what
  // a shared link needs to restore.
  const [search, setSearch] = useState("");
  const [advanced, setAdvanced] = useState(false);

  useUrlState(
    {
      // A single-store console keeps its place out of the URL: there is
      // nothing to deep-link to and nothing to restore.
      gov: single ? "" : place.governorate ?? "",
      area: single ? "" : place.area ?? "",
      branch: single ? "" : place.branchIds.join(","),
      from: range.from,
      to: range.to,
      demo: includeDemo ? "1" : "",
      faceid: excludeStaff ? "1" : "",
    },
    (p) => {
      if (!single && ("gov" in p || "area" in p || "branch" in p)) {
        setPlace({
          governorate: ("gov" in p ? p.gov : place.governorate ?? "") || null,
          area: ("area" in p ? p.area : place.area ?? "") || null,
          branchIds: "branch" in p ? parseBranchParam(p.branch) : place.branchIds,
        });
      }
      if ("from" in p || "to" in p) {
        setRange({ from: p.from || range.from, to: p.to || range.to });
      }
      if ("demo" in p) setIncludeDemo(p.demo === "1");
      if ("faceid" in p) setExcludeStaff(p.faceid === "1");
    },
  );

  // With the demo toggle off, demo stores disappear from the drill-down and
  // demo-only places (e.g. "Demo Governorate") are dropped entirely.
  const visibleGovernorates = governorates
    .map((g) => ({ ...g, branches: includeDemo ? g.branches : g.branches - g.demo }))
    .filter((g) => g.branches > 0);
  const areas = (
    visibleGovernorates.find((g) => g.governorate === place.governorate)?.areas ?? []
  )
    .map((a) => ({ ...a, branches: includeDemo ? a.branches : a.branches - a.demo }))
    .filter((a) => a.branches > 0);
  const visibleBranches = branchesInPlace.filter((b) => includeDemo || !b.is_demo);
  const configuredOnly = visibleBranches.filter(isConfigured);
  // The search looks across every store, not just the drilled-into place —
  // that is the point of typing a name instead of stepping down to it.
  const searchable = branches
    .filter((b) => includeDemo || !b.is_demo)
    .map((b) => ({
      id: b.id,
      name: b.name,
      governorate: b.governorate,
      area: b.area,
      note: isConfigured(b) ? null : "no camera",
    }));

  // The rows: the selected stores in selection order, one colour each.
  const rows = place.branchIds.map((id, index) => {
    const branch = branches.find((b) => b.id === id);
    return {
      id,
      name: branch?.name ?? id,
      place: branch ? `${branch.governorate} › ${branch.area || "—"}` : "",
      color: chartSeries[index % chartSeries.length],
    };
  });
  const addable = searchable.filter((b) => !place.branchIds.includes(b.id));

  /** Add a store row. Rows are an explicit list, so the governorate/area
   *  narrowing is dropped: the API intersects ids with the place, which
   *  would silently hide a store from elsewhere. */
  const addRow = (id: string) => {
    if (place.branchIds.includes(id) || place.branchIds.length >= MAX_COMPARE) return;
    setPlace({ governorate: null, area: null, branchIds: [...place.branchIds, id] });
  };
  const removeRow = (id: string) => {
    setPlace({ ...place, branchIds: place.branchIds.filter((x) => x !== id) });
  };
  const isPreset = (preset: (typeof PRESETS)[number]) => {
    const r = preset.range();
    return r.from === range.from && r.to === range.to;
  };

  return (
    <div className={`${ui.card} flex flex-col gap-3 px-4 py-3`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={ui.label}>Range</span>
          {PRESETS.map((preset) => (
            <button
              type="button"
              key={preset.label}
              className={`${ui.chip} ${isPreset(preset) ? ui.chipOn : ""}`}
              aria-pressed={isPreset(preset)}
              onClick={() => setRange(preset.range())}
            >
              {preset.label}
            </button>
          ))}
          <input
            className={ui.input}
            type="date"
            aria-label="From"
            value={range.from}
            max={range.to}
            onChange={(event) => event.target.value && setRange({ ...range, from: event.target.value })}
          />
          <span className="text-muted">→</span>
          <input
            className={ui.input}
            type="date"
            aria-label="To"
            value={range.to}
            min={range.from}
            onChange={(event) => event.target.value && setRange({ ...range, to: event.target.value })}
          />
        </div>

        {hasDemo && (
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Demo</span>
            <button
              type="button"
              className={`${ui.chip} ${includeDemo ? ui.chipOn : ""}`}
              aria-pressed={includeDemo}
              title="Include the demo store's data in every chart and total"
              onClick={() => setIncludeDemo(!includeDemo)}
            >
              {includeDemo ? "Demo store included" : "Demo store excluded"}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-2">
        <button
          type="button"
          className="flex w-fit items-center gap-1.5 text-muted hover:text-text"
          aria-expanded={advanced}
          onClick={() => setAdvanced(!advanced)}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 12 12"
            className={`h-2.5 w-2.5 transition-transform ${advanced ? "rotate-90" : ""}`}
          >
            <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className={ui.label}>Advanced</span>
        </button>
        {advanced && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Switch
              label="Staff Face ID Exclusion"
              checked={excludeStaff}
              title="Take out the people recognised as enrolled staff by their face (Store › Tracks). The store always reports everyone; this only changes what the dashboard shows. The staff percentage then applies to what is left."
              onChange={setExcludeStaff}
            />
            <label
              className="flex flex-wrap items-center gap-2"
              title="Take this percentage off every count, for staff the cameras cannot recognise. Display only — nothing is written back."
            >
              <span className={ui.label}>Staff percentage</span>
              <input
                type="range"
                min={MIN_STAFF_PERCENT}
                max={MAX_STAFF_PERCENT}
                step={1}
                value={staffPercent}
                aria-valuetext={`${staffPercent}%`}
                className="h-1.5 w-36 cursor-pointer accent-accent"
                onChange={(event) => setStaffPercent(Number(event.target.value))}
              />
              <span
                className={`w-12 text-[13px] tabular-nums ${staffPercent > 0 ? "font-semibold text-accent" : "text-muted"}`}
              >
                {staffPercent}%
              </span>
              {staffPercent !== DEFAULT_STAFF_PERCENT && (
                <button
                  type="button"
                  className={ui.btnQuiet}
                  onClick={() => setStaffPercent(DEFAULT_STAFF_PERCENT)}
                >
                  Reset
                </button>
              )}
            </label>
          </div>
        )}
      </div>

      {!single && (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Branches</span>
            {rows.length === 0 && (
              <>
                <Select
                  aria-label="Governorate"
                  value={place.governorate ?? ""}
                  onChange={(e) =>
                    setPlace({ governorate: e.target.value || null, area: null, branchIds: [] })
                  }
                >
                  <option value="">All governorates</option>
                  {visibleGovernorates.map((g) => (
                    <option key={g.governorate} value={g.governorate}>
                      {g.governorate} ({g.branches})
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label="Area"
                  value={place.area ?? ""}
                  disabled={!place.governorate}
                  onChange={(e) =>
                    setPlace({ ...place, area: e.target.value || null, branchIds: [] })
                  }
                >
                  <option value="">All areas</option>
                  {areas.map((a) => (
                    <option key={a.area} value={a.area}>
                      {a.area} ({a.branches})
                    </option>
                  ))}
                </Select>
                {place.governorate && (
                  <span className="text-xs text-muted">
                    {configuredOnly.length} of {visibleBranches.length} with a camera
                  </span>
                )}
              </>
            )}
            {rows.length > 0 && (
              <span className="text-xs text-muted">
                {rows.length === 1
                  ? "One store. Add another row to compare."
                  : `${rows.length} stores compared — one colour each.`}
              </span>
            )}
            {(place.governorate || rows.length > 0) && (
              <button
                type="button"
                className={ui.chip}
                onClick={() => {
                  setSearch("");
                  setPlace(NO_PLACE);
                }}
              >
                Clear
              </button>
            )}
          </div>

          <ol className="flex flex-col gap-1.5">
            {rows.map((row, index) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-bg px-3 py-2"
              >
                <span className="w-5 text-xs text-muted tabular-nums">{index + 1}</span>
                <span
                  aria-hidden="true"
                  className="inline-block size-3 rounded-full ring-2 ring-surface"
                  style={{ background: row.color }}
                />
                <span className="font-medium">{row.name}</span>
                <span className="text-xs text-muted">{row.place}</span>
                <button
                  type="button"
                  className="ml-auto inline-flex size-6 cursor-pointer items-center justify-center rounded-full leading-none text-muted hover:bg-[color-mix(in_srgb,var(--danger)_12%,transparent)] hover:text-danger"
                  aria-label={`Remove ${row.name} from the dashboard`}
                  title="Remove this row"
                  onClick={() => removeRow(row.id)}
                >
                  ×
                </button>
              </li>
            ))}
            <li className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border px-3 py-2">
              <span className="w-5 text-xs text-muted tabular-nums">{rows.length + 1}</span>
              <span aria-hidden="true" className="inline-block size-3 rounded-full border border-dashed border-muted" />
              {rows.length < MAX_COMPARE ? (
                <BranchSearch
                  className="min-w-[280px] flex-1 max-w-[420px]"
                  aria-label="Add a branch row"
                  placeholder={rows.length === 0 ? "Add a branch — type its name, area or id" : "Add another branch to compare"}
                  branches={addable}
                  value={search}
                  onChange={setSearch}
                  onSelect={(branch) => {
                    setSearch("");
                    addRow(branch.id);
                  }}
                />
              ) : (
                <span className="text-xs text-muted">Up to {MAX_COMPARE} branches at once</span>
              )}
              {rows.length === 0 && (
                <span className="text-xs text-muted">
                  Add a branch to see its numbers — the dashboard stays empty until you do.
                </span>
              )}
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
